// Copyright ©️ 2026 eVolve MEP, LLC

/// <reference types="forge-viewer" />

import { useEffect, useRef } from 'react';

const { Autodesk } = window;

/**
 * Manages the core lifecycle refs for an Autodesk GuiViewer3D instance:
 * - `mounted` ref to guard against post-unmount operations
 * - `viewer` ref holding the GuiViewer3D instance
 * - `viewerRuntimePromise` ref for the Initializer promise
 * - Cleanup effect that tears down the viewer on unmount
 */
export const useViewer = () => {
  const mounted = useRef(true);
  const viewer = useRef<Autodesk.Viewing.GuiViewer3D>(undefined);
  const viewerRuntimePromise = useRef<Promise<void> | null>(null);

  useEffect(
    () => () => {
      mounted.current = false;
      viewer.current?.forEachExtension((ext) => viewer.current?.unloadExtension(ext.getName()));
      viewer.current?.getAllModels().forEach((m) => viewer.current?.unloadModel(m));
      viewer.current?.tearDown();
      viewer.current?.finish();
      viewer.current = undefined;
      Autodesk.Viewing.shutdown();
      viewerRuntimePromise.current = null;
    },
    [],
  );

  return { mounted, viewer, viewerRuntimePromise };
};
